// One agent RUN = every LLM call that agent made inside one turn. The header
// carries the run's totals (calls, duration, tokens, cost, when, whose project);
// each call below it is a collapsible step whose summary shows model / duration /
// tokens / finish reason, and which expands to the exact input messages, output
// text, tool calls, and per-call settings.
//
// Shapes are the GENERATED wire types (`AgentRunDTO` / `TraceStepDTO`); the jsonb
// columns arrive as `Record<string, unknown>`, which is why every value goes
// through `asText` rather than being interpolated directly.

import type { AgentRunDTO, TraceStepDTO } from "@recruiter/shared/types";

import { agentLabel } from "../lib/debug-agents";
import { asText, formatDuration, runDurationMs, traceMessageView } from "../lib/debug-trace";
import { formatDateTime } from "../lib/format";

function Messages({ messages }: { messages: TraceStepDTO["input_messages"] }) {
  if (!messages || messages.length === 0) {
    return <p className="dbg-muted">No input messages captured.</p>;
  }
  return (
    <div className="dbg-messages">
      {messages.map((message, i) => {
        const { role, body } = traceMessageView(message);
        // Class slug from the role, spaces to dashes ("tool result" →
        // "tool-result"), so each role can be tinted from CSS alone.
        const slug = role.replace(/\s+/g, "-");
        return (
          <div key={i} className={`dbg-msg dbg-msg--${slug}`}>
            <span className="dbg-msg__role">{role}</span>
            <pre className="dbg-pre">{body}</pre>
          </div>
        );
      })}
    </div>
  );
}

function ToolCalls({ calls }: { calls: TraceStepDTO["output_tool_calls"] }) {
  if (!calls || calls.length === 0) return null;
  return (
    <div className="dbg-tools">
      <h5 className="dbg-subhead">Tool calls / decisions</h5>
      {calls.map((call, i) => {
        const rec = call as Record<string, unknown>;
        const name = asText(rec.name) || asText(rec.type) || "tool";
        return (
          <div key={i} className="dbg-tool">
            <code className="dbg-tool__name">{name}</code>
            <pre className="dbg-pre">{asText(rec.arguments)}</pre>
          </div>
        );
      })}
    </div>
  );
}

function StepBody({ step }: { step: TraceStepDTO }) {
  return (
    <div className="dbg-step__body">
      <div className="dbg-metrics">
        <span>
          model: <b>{step.model ?? "-"}</b>
        </span>
        <span>
          finish: <b>{step.finish_reason ?? "-"}</b>
        </span>
        <span>
          tokens: <b>{step.total_tokens ?? 0}</b>
        </span>
        <span>
          cost: <b>${(step.cost_usd ?? 0).toFixed(4)}</b>
        </span>
        <span>
          duration: <b>{formatDuration(step.latency_ms)}</b>
        </span>
        <span>
          label: <b>{step.agent_label}</b>
        </span>
      </div>
      <div className="dbg-metrics">
        <span>
          effort: <b>{step.reasoning_effort ?? "-"}</b>
        </span>
        <span>
          temp: <b>{step.temperature ?? "-"}</b>
        </span>
        <span>
          max_tok: <b>{step.max_tokens ?? "-"}</b>
        </span>
        <span>
          tool_choice: <b>{step.tool_choice ?? "-"}</b>
        </span>
        <span>
          format: <b>{step.response_format ?? "-"}</b>
        </span>
        <span>
          tools: <b>{step.tool_count ?? "-"}</b>
        </span>
        <span>
          stream: <b>{step.streamed == null ? "-" : step.streamed ? "yes" : "no"}</b>
        </span>
      </div>
      <h5 className="dbg-subhead">Input (prompt + context + history)</h5>
      <Messages messages={step.input_messages} />
      {step.output_text ? (
        <>
          <h5 className="dbg-subhead">Output</h5>
          <pre className="dbg-pre dbg-pre--out">{step.output_text}</pre>
        </>
      ) : null}
      <ToolCalls calls={step.output_tool_calls} />
      {step.error ? <p className="dbg-error">Error: {step.error}</p> : null}
    </div>
  );
}

export function DebugRunCard({
  run,
  defaultOpen = false,
}: {
  run: AgentRunDTO;
  defaultOpen?: boolean;
}) {
  const owner = run.owner_display_name ?? run.owner_email ?? null;
  const project = run.project_name ?? run.project_id ?? null;
  return (
    <article className="dbg-run">
      <header className="dbg-run__head">
        <span className="dbg-run__agent">{agentLabel(run.agent)}</span>
        <span className="dbg-run__meta">
          {run.call_count} call{run.call_count === 1 ? "" : "s"} ·{" "}
          {formatDuration(runDurationMs(run.steps))} · {run.total_tokens} tok · $
          {run.total_cost_usd.toFixed(4)} · {formatDateTime(run.started_at)}
        </span>
        {project || owner ? (
          <span className="dbg-run__who">
            {project ? <span className="dbg-run__project">{project}</span> : null}
            {owner ? <span className="dbg-run__user">{owner}</span> : null}
          </span>
        ) : null}
        {run.turn_id ? <span className="dbg-run__turn">turn {run.turn_id}</span> : null}
      </header>
      <div className="dbg-run__steps">
        {run.steps.map((step, i) => (
          <details key={step.id} className="dbg-step" open={defaultOpen && i === 0}>
            <summary className="dbg-step__summary">
              <span>
                Call {i + 1}
                {/* The raw agent_label, shown only when it differs from the run's
                    canonical brain — e.g. `search_intent` grouped under SCOUT.
                    It marks a DISTINCT operation, not another loop iteration. */}
                {step.agent_label && step.agent_label !== run.agent ? (
                  <span
                    className="dbg-step__op"
                    title="raw agent label: a distinct operation grouped under this brain"
                  >
                    {step.agent_label}
                  </span>
                ) : null}
              </span>
              <span className="dbg-step__summary-meta">
                {step.model ?? "-"} · {formatDuration(step.latency_ms)} · {step.total_tokens ?? 0}{" "}
                tok · {step.finish_reason ?? "-"}
              </span>
            </summary>
            <StepBody step={step} />
          </details>
        ))}
      </div>
    </article>
  );
}
